import {
    Box,
    BoxProps,
    Button,
    ButtonProps,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardProps,
    IconButton,
    Modal,
    PaperProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    TextFieldProps,
    Typography,
    useTheme
} from "@mui/material";
import React, { Dispatch, useEffect } from "react";
import { getAllLocations, updateNameLocation, deleteNameLocation, reActivate, createLocation } from "services/location-service";
import * as Styles from "pages/development/settings/tabs/locations/listLocations.styles";
import { useTranslation } from "react-i18next";
import CloseIcon from '@mui/icons-material/Close';
// import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check'
import Clear from '@mui/icons-material/Clear'
import { IPropsSettings } from "../../Settings";

export default function ListLocations(props: IPropsSettings) {
    const { tenantId } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [locations, setLocations] = React.useState<AdminLocationList[]>([]);
    const [createError, setCreateError] = React.useState<string>('');
    const [openCreate, setOpenCreate] = React.useState<boolean>(false);
    const [newLocationName, setNewLocationName] = React.useState<string>('');

    const handleOpenCreate = (): void => setOpenCreate(true);
    const handleCloseCreate = (): void => setOpenCreate(false);

    useEffect(() => {
        getAllLocations(tenantId).then((res) => {
            setLocations(res);
        })
    }, [tenantId])

    const updateLocation = (location: AdminLocationList) => {
        const newLocations = locations.map((loc) => {
            if (loc.id === location.id) return location
            return loc
        })
        setLocations(newLocations.sort((a, b) => (+a.isDeleted) - (+b.isDeleted) || a.name.localeCompare(b.name)));
    }

    const handleCreateLocation = (): void => {
        createLocation(newLocationName, tenantId)
            .then(res => {
                const newLocations = [...locations, res]
                newLocations.sort((a, b) => (+a.isDeleted) - (+b.isDeleted) || a.name.localeCompare(b.name));
                setLocations(newLocations)
                handleCloseCreate();
            })
            .catch(err => {
                setCreateError(err.response.data.error.message);
            })
    }

    const listLocationWrapperProps: PaperProps = {
        sx: Styles.listRequestsWrapper(theme)
    }

    const titleWrapperProps: BoxProps = {
        sx: Styles.titleWrapper(theme)
    }

    const handleChange = (e: any): void => {
        setNewLocationName(e.target.value)
    }

    const CreateProps: TextFieldProps = {
        defaultValue: '',
        onChange: handleChange
    }

    const modalProps: CardProps = {
        variant: 'outlined',
        sx: Styles.modal(theme)
    }

    const buttonProps: ButtonProps = {
        variant: 'contained',
        sx: Styles.createButton(theme)
    }

    return (
        <Box>
            <Typography variant='h4'>
                {t('settings.locations.title')}
            </Typography>
            <Box>
                {t('settings.locations.description')}
            </Box>
            <Card {...listLocationWrapperProps}>
                <Box {...titleWrapperProps}>
                    <Button {...buttonProps} onClick={handleOpenCreate}>{t('settings.locations.new')}</Button>
                </Box>
                <TableContainer>
                    <Table sx={{ minWidth: 700 }} aria-label="customized table" size="small" >
                        <TableBody>
                            {locations.map((loc) => (
                                <LocationRow key={loc.id} location={loc} updateLocation={updateLocation} tenantId={tenantId} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Modal
                    open={openCreate}
                    onClose={handleCloseCreate}
                    aria-labelledby={`create-title`}
                    aria-describedby={`create-description`}
                >
                    <Card {...modalProps}>
                        <CardHeader
                            title={t('settings.locations.create')}
                            action={
                                <IconButton onClick={handleCloseCreate}><CloseIcon /></IconButton>
                            }
                        />
                        <CardContent>
                            <Box>
                                <TextField {...CreateProps} /><br />
                                {createError !== '' &&
                                    <Box component={'div'} sx={{color: 'red'}}>{t(createError)}</Box>
                                }
                            </Box>
                        </CardContent>
                        <CardActions>
                            <Button variant='contained' color='cancel' onClick={handleCloseCreate}>{t('buttons.cancel')}</Button>
                            <Button variant='contained' color='success' onClick={handleCreateLocation} disabled={newLocationName === undefined || newLocationName.length === 0}>{t('settings.locations.create')}</Button>
                        </CardActions>
                    </Card>
                </Modal>
            </Card>
        </Box>
    )
}

interface locationRowProps {
    location: AdminLocationList,
    updateLocation: Dispatch<AdminLocationList>,
    tenantId: string
};

function LocationRow(props: locationRowProps) {
    const theme = useTheme();
    const { t } = useTranslation();
    const { location, updateLocation, tenantId } = props
    const [newName, setNewName] = React.useState<string>('');

    const [openEdit, setOpenEdit] = React.useState<boolean>(false);
    const [openDelete, setOpenDelete] = React.useState<boolean>(false);
    const [openActivate, setOpenActivate] = React.useState<boolean>(false);
    const handleEditOpen = () => setOpenEdit(true);
    const handleEditClose = () => setOpenEdit(false);
    const handleDeleteClose = () => setOpenDelete(false);
    const handleActivateClose = () => setOpenActivate(false);

    const handleDelete = (): void => {
        deleteNameLocation(location.id, tenantId).then(res => {
            const temp: AdminLocationList = location;
            temp.isDeleted = true;
            updateLocation(temp);
            handleEditClose();
            handleDeleteClose();
        })
    }

    const handleEdit = (): void => {
        updateNameLocation(location.id, newName, tenantId).then(res => {
            updateLocation(res);
            handleEditClose();
        })
    }

    const handleActivate = (): void => {
        reActivate(location.id, tenantId).then(res => {
            updateLocation(res);
            handleEditClose();
            handleActivateClose();
        })
    }

    const handleChange = (e: any) => {
        setNewName(e.target.value)
    }

    const modalProps: CardProps = {
        variant: 'outlined',
        sx: Styles.modal(theme)
    }

    const editProps: TextFieldProps = {
        defaultValue: location.name,
        size: 'small',
        onChange: handleChange,
        sx: Styles.editTextField(theme),
    }

    const cancelProps: ButtonProps = {
        sx: Styles.cancelButton(theme),
        variant: 'contained',
        onClick: handleEditClose
    }


    return (
        <TableRow hover>
            {/* <TableCell align="left" sx={{width: '0rem'}}>
                {location.isDeleted ?
                    <Clear color="error" /> :
                    <CheckIcon color="success" />
                }
            </TableCell> */}
            <TableCell component="th" scope="row">
                {location.name}
            </TableCell>
            <TableCell align="right"><IconButton onClick={handleEditOpen}><EditIcon /></IconButton></TableCell>
            <TableCell align="right" sx={{width: '0rem'}}>
                {location.isDeleted ?
                    <Clear color="error" /> :
                    <CheckIcon color="success" />
                }
            </TableCell>
            <Modal
                open={openEdit}
                onClose={handleEditClose}
                aria-labelledby={`${location.id}-edit-title`}
                aria-describedby={`${location.id}-edit-description`}
            >
                <Card {...modalProps}>
                    <CardHeader
                        title={location.name}
                        action={
                            <IconButton onClick={handleEditClose}><CloseIcon /></IconButton>
                        }
                    />
                    <CardContent>
                        <TextField {...editProps} />
                    </CardContent>
                    <CardActions>
                        <Button {...cancelProps}>Cancel</Button>
                        { location.isDeleted ?
                            <Button variant="contained" color="success" onClick={handleActivate}>{t('settings.locations.activate')}</Button>:
                            <Button variant="contained" color="warning" onClick={handleDelete}>{t('settings.locations.deactivate')}</Button>
                        }
                        <Button variant="contained" onClick={handleEdit} disabled={newName === undefined || newName.length === 0 || newName === location.name}>{t('settings.locations.change')}</Button>
                    </CardActions>
                </Card>
            </Modal>
            <Modal
                open={openDelete}
                onClose={handleDeleteClose}
                aria-labelledby={`${location.id}-delete-title`}
                aria-describedby={`${location.id}-delete-description`}
            >
                <Card {...modalProps}>
                    <CardHeader
                        title={location.name}
                        action={
                            <IconButton onClick={handleDeleteClose}><CloseIcon /></IconButton>
                        }
                    />
                    <CardContent>
                        <Typography id={`${location.id}-delete-description`} sx={{ mt: 2 }}>
                            {t("settings.locations.deleteConfirmation")}
                        </Typography>
                    </CardContent>
                    <CardActions>
                        <Button variant="contained" color="warning" onClick={handleDelete}>{t('settings.locations.deactivate')}</Button>
                        <Button variant="contained" sx={Styles.cancelButton(theme)} onClick={handleDeleteClose}>Cancel</Button>
                    </CardActions>
                </Card>
            </Modal>
            <Modal
                open={openActivate}
                onClose={handleActivateClose}
                aria-labelledby={`${location.id}-active-title`}
                aria-describedby={`${location.id}-active-description`}
            >
                <Card {...modalProps}>
                    <CardHeader
                        title={location.name}
                        action={
                            <IconButton onClick={handleActivateClose}><CloseIcon /></IconButton>
                        }
                    />
                    <CardContent>
                        <Typography id={`${location.id}-active-description`} sx={{ mt: 2 }}>
                            {t("settings.locations.activeConfirmation")}
                        </Typography>
                    </CardContent>
                    <CardActions>
                        <Button variant="contained" color="success" onClick={handleActivate}>{t('settings.locations.activate')}</Button>
                        <Button variant="contained" sx={Styles.cancelButton(theme)} onClick={handleActivateClose}>Cancel</Button>
                    </CardActions>
                </Card>
            </Modal>
        </TableRow>
    )
}
